import React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { clsx } from 'clsx'

const buttonVariants = cva(
    // Base styles
    'inline-flex items-center justify-center font-medium rounded-md transition-colors outline-none focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed',
    {
        variants: {
            variant: {
                solid: 'text-white',
                outline: 'border-2 bg-transparent',
                icon: 'bg-transparent',
            },
            color: {
                primary: '',
                success: '',
                danger: '',
                warn: '',
                neutral: '',
            },
            size: {
                md: 'px-4 py-2 text-sm gap-2',
                lg: 'px-6 py-3 text-base gap-3',
                sm: 'px-3 py-1.5 text-sm gap-2',
            },
        },
        compoundVariants: [
            // Solid variant colors
            {
                variant: 'solid',
                color: 'primary',
                className: 'bg-primary-600 hover:bg-primary-700 focus:ring-primary-500',
            },
            {
                variant: 'solid',
                color: 'success',
                className: 'bg-success-600 hover:bg-success-700 focus:ring-success-500',
            },
            {
                variant: 'solid',
                color: 'danger',
                className: 'bg-danger-600 hover:bg-danger-700 focus:ring-danger-500',
            },
            {
                variant: 'solid',
                color: 'warn',
                className: 'bg-warn-600 hover:bg-warn-700 focus:ring-warn-500',
            },
            {
                variant: 'solid',
                color: 'neutral',
                className: 'bg-gray-600 hover:bg-gray-700 focus:ring-gray-500 dark:bg-gray-700 dark:hover:bg-gray-600',
            },

            // Outline variant colors
            {
                variant: 'outline',
                color: 'primary',
                className: 'border-primary-600 text-primary-600 dark:border-primary-500 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 focus:ring-primary-500',
            },
            {
                variant: 'outline',
                color: 'success',
                className: 'border-success-600 text-success-600 dark:border-success-500 dark:text-success-400 hover:bg-success-50 dark:hover:bg-success-900/20 focus:ring-success-500',
            },
            {
                variant: 'outline',
                color: 'danger',
                className: 'border-danger-600 text-danger-600 dark:border-danger-500 dark:text-danger-400 hover:bg-danger-50 dark:hover:bg-danger-900/20 focus:ring-danger-500',
            },
            {
                variant: 'outline',
                color: 'warn',
                className: 'border-warn-600 text-warn-600 dark:border-warn-500 dark:text-warn-400 hover:bg-warn-50 dark:hover:bg-warn-900/20 focus:ring-warn-500',
            },
            {
                variant: 'outline',
                color: 'neutral',
                className: 'border-gray-300 text-gray-700 dark:border-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:ring-gray-500',
            },
            // Icon variant colors
            {
                variant: 'icon',
                color: 'primary',
                className: 'text-primary-600 hover:text-primary-800 dark:text-primary-500 dark:hover:text-primary-300 focus:ring-primary-500',
            },
            {
                variant: 'icon',
                color: 'success',
                className: 'text-success-600 hover:text-success-800 dark:text-success-500 dark:hover:text-success-300 focus:ring-success-500',
            },
            {
                variant: 'icon',
                color: 'danger',
                className: 'text-danger-600 hover:text-danger-800 dark:text-danger-500 dark:hover:text-danger-300 focus:ring-danger-500',
            },
            {
                variant: 'icon',
                color: 'warn',
                className: 'text-warn-600 hover:text-warn-800 dark:text-warn-500 dark:hover:text-warn-300 focus:ring-warn-500',
            },
            {
                variant: 'icon',
                color: 'neutral',
                className: 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 focus:ring-gray-500',
            },
            // Icon size adjustments
            {
                variant: 'icon',
                size: 'sm',
                className: '[&]:px-1 [&]:py-1.5',
            },
            {
                variant: 'icon',
                size: 'md',
                className: '[&]:px-1.5',
            },
            {
                variant: 'icon',
                size: 'lg',
                className: '[&]:px-2 [&]:py-3',
            },
        ],
        defaultVariants: {
            variant: 'solid',
            color: 'primary',
            size: 'md',
        },
    }
)

export interface ButtonProps
    extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'color'>,
    VariantProps<typeof buttonVariants> {
    asChild?: boolean
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    ({ className, variant, color, size, ...props }, ref) => {
        return (
            <button
                className={clsx(buttonVariants({ variant, color, size }), className)}
                ref={ref}
                {...props}
            />
        )
    }
)

Button.displayName = 'Button'
